import type { ScenarioSpec } from "./types";

/**
 * Sample scenario: phishing (typosquatted bank login) -> fake "account drained /
 * restore access" panic that harvests a phone number -> live vishing call that
 * steals the one-time code. Each risky step is followed by a teach moment.
 *
 * Asset URLs point at our own VPS (`/cdn/sim/...`). For the PoC before a real
 * Marble export exists, the loader falls back to a procedural office + a solid
 * backdrop (see Environment3D), so this runs with empty/placeholder assets.
 */
export const phishingOfficeScenario: ScenarioSpec = {
  version: 1,
  id: "phishing-office",
  title: "The Bank That Wasn't",
  summary: {
    goal: "Learn to spot a phishing email, a fake login page and a fake bank call, and the one check that stops each step.",
    youDo: "Read your inbox, follow a link to a sign-in page, react to an account warning and answer a call on your desk phone.",
    duration: "7-9 min",
    tags: ["email_phishing", "fake_login_pages", "voice_phishing", "mfa", "verification"],
    audience: ["all_staff"],
    needs: ["sound"],
  },
  topic: "Phishing -> fake account lockout -> voice fraud (vishing)",
  language: "en",

  scene: {
    environment: {
      splat: {
        high: { url: "/cdn/sim/phishing-office/office_2m.spz", format: "spz", approxBytes: 38_000_000 },
        mid: { url: "/cdn/sim/phishing-office/office_500k.spz", format: "spz", approxBytes: 9_000_000 },
        low: { url: "/cdn/sim/phishing-office/office_pano.jpg", format: "jpg", approxBytes: 1_200_000 },
      },
      colliderMesh: { url: "/cdn/sim/phishing-office/office_collider.glb", format: "glb" },
      hdri: { url: "/cdn/sim/phishing-office/office.hdr", format: "hdr" },
      backdropColor: "#0d1014",
      procedural: "office",
    },
    props: [
      {
        id: "monitor",
        label: "Desk monitor",
        asset: { url: "/cdn/sim/phishing-office/monitor.glb", format: "glb", compression: "draco" },
        position: [0, 0.95, -0.35],
        hostsDevice: "desktop",
        styleAnchorId: "office-stylelock-v1",
      },
      {
        id: "deskphone",
        label: "Desk phone",
        asset: { url: "/cdn/sim/phishing-office/deskphone.glb", format: "glb", compression: "draco" },
        position: [0.55, 0.95, -0.1],
        rotationDeg: [0, -35, 0],
        hostsDevice: "phone",
        styleAnchorId: "office-stylelock-v1",
      },
    ],
    waypoints: [
      { id: "wp_establish", position: [2.6, 1.9, 2.8], target: [0, 1.0, -0.3], fov: 50, durationS: 0, ease: "easeInOut" },
      // Dead-on the monitor screen (centre ~[0,0.95,-0.33], upright) so the DOM
      // desktop (62vh, aspect 1.706) lands exactly on it across all window sizes.
      { id: "wp_monitor", position: [0, 0.95, 0.52], target: [0, 0.95, -0.33], fov: 36, durationS: 2.0, ease: "easeInOut" },
      { id: "wp_pullback", position: [1.4, 1.6, 1.6], target: [0.3, 1.0, -0.2], fov: 46, durationS: 1.6, ease: "easeInOut" },
      { id: "wp_phone", position: [0.65, 1.15, 0.45], target: [0.55, 0.97, -0.1], fov: 34, durationS: 1.4, ease: "easeOut" },
    ],
    initialWaypointId: "wp_establish",
  },

  devices: [
    {
      id: "desktop",
      kind: "desktop",
      config: {
        clock: "9:11 AM",
        date: "24/06/2026",
        icons: [
          { id: "chrome", label: "Browser", icon: "chrome", opens: "browser" },
          { id: "mail", label: "Mail", icon: "mail", opens: "mail" },
          { id: "files", label: "Files", icon: "files" },
          { id: "settings", label: "Settings", icon: "settings" },
        ],
        taskbar: [
          { id: "tb_chrome", label: "Browser", icon: "chrome", opens: "browser" },
          { id: "tb_mail", label: "Mail", icon: "mail", opens: "mail" },
          { id: "tb_files", label: "Files", icon: "files" },
        ],
      },
    },
    {
      id: "mail",
      kind: "mail",
      config: {
        appTitle: "Outlook",
        emptyTitle: "Open the YourBank Security email",
        emptyHint: "Select the urgent message from YourBank Security in the list on the left.",
        emails: [
          {
            id: "e_alert",
            from: "YourBank Security",
            fromAddress: "alerts@youbrank.com",
            to: "alice@company.com",
            subject: "Unusual activity on your account — verify now",
            preview:
              "We detected unusual activity on your YourBank account. Sign in to review and secure it…",
            receivedAt: "9:12 AM",
            unread: true,
            initials: "YB",
            lure: true,
            cta: {
              label: "Review your account",
              url: "https://youbrank.com/login",
              displayUrl: "https://www.yourbank.com/secure-login",
            },
            body:
              "Dear Customer,\n\n" +
              "We detected unusual activity on your YourBank account this morning, including sign-in attempts from a new device.\n\n" +
              "To protect your funds, please review your recent activity and confirm it was you. Sign in here:\n\n" +
              "If you do not verify within 24 hours, access to your account may be suspended.\n\n" +
              "Thank you,\nYourBank Security Team",
          },
          {
            id: "e_hr",
            from: "People Team",
            fromAddress: "people@company.com",
            subject: "Benefits open enrollment starts Monday",
            preview: "A quick reminder that open enrollment opens next week…",
            receivedAt: "8:40 AM",
            initials: "PT",
            body:
              "Hi Alice,\n\nOpen enrollment for benefits starts Monday. No action is needed today — we'll share the portal link then.\n\n— People Team",
          },
          {
            id: "e_news",
            from: "Industry Weekly",
            fromAddress: "digest@industryweekly.com",
            subject: "5 stories you should read this week",
            preview: "This week in tech: the stories shaping the industry…",
            receivedAt: "Mon",
            initials: "IW",
            body: "Your weekly digest of the five stories worth your time.\n\nRead online for the full roundup.",
          },
        ],
      },
    },
    {
      id: "browser",
      kind: "browser",
      config: {
        // The email link "opened" the browser already on the spoofed login.
        homeUrl: "youbrank.com/login",
        targetUrl: "youbrank.com/login",
        loadsPage: "phishing_login",
        phishing: {
          brand: "YourBank",
          spoofUrl: "https://youbrank.com/login",
          legitDomain: "yourbank.com",
          headline: "Sign in to YourBank",
          redFlags: [
            "The domain is `youbrank.com` — a letter-swap of `yourbank.com` (typosquatting). One transposed letter is easy to miss.",
            "You arrived here from an email link, not a bookmark or the bank's app.",
            "Urgency ('verify now', 'access may be suspended') is engineered to stop you checking the address bar.",
          ],
        },
      },
    },
    {
      id: "voicebrief",
      kind: "browser",
      config: {
        homeUrl: "learn.northwind.example/voice-phishing",
        targetUrl: "learn.northwind.example/voice-phishing",
        loadsPage: "blank",
        awarenessVideo: {
          url: "/cdn/sim/awareness-videos/voice-phishing/voice-phishing.mp4",
          brand: "Security awareness",
          headline: "Voice phishing",
          body:
            "A caller can sound like your bank and still be after the code that signs them in. This short film shows how that call works, and what to do instead.",
          playLabel: "Watch the video",
          continueLabel: "Continue to quiz",
        },
      },
    },
    {
      id: "ebanking",
      kind: "ebanking",
      config: {
        brand: "YourBank",
        url: "youbrank.com/account",
        accountName: "Alice Morgan",
        accounts: [
          { name: "Everyday Checking", number: "•••• 4821", balance: "$0.00", sub: "Available balance", danger: true },
          { name: "Savings", number: "•••• 9377", balance: "$0.00", sub: "Available balance", danger: true },
        ],
        transactions: [
          { date: "Today 9:08", desc: "Outgoing wire transfer", amount: "\u2212$18,420.00", negative: true, tag: "PENDING" },
          { date: "Today 9:07", desc: "Transfer to external account", amount: "\u2212$6,250.00", negative: true, tag: "PENDING" },
          { date: "Yesterday", desc: "Corner Market", amount: "\u2212$72.40", negative: true },
        ],
        notification: {
          title: "Account access restricted",
          body: "Suspicious transfers detected — your account is temporarily locked",
        },
        verify: {
          title: "Verify your identity to restore access",
          body:
            "Due to a technical issue, access to your account is restricted and recent transfers are on hold. To reverse them and restore your funds, confirm the mobile number where our fraud team can reach you to complete verification.",
          phoneLabel: "Mobile phone number",
          phonePlaceholder: "+1 (415) 555-0142",
          cta: "Restore my access",
        },
      },
    },
    {
      id: "phone",
      kind: "phone",
      config: {
        lockClock: "9:14 AM",
        callerName: "YourBank Fraud Dept",
        callerSubtitle: "+1 (650) 555-0188",
        mfaCode: "8042",
        mfaPrompt: "Your authenticator app shows:",
      },
    },
  ],

  voiceAttackers: {
    fraud_dept: {
      persona: {
        name: "Dave",
        role: "Bank fraud department agent",
        pretext: "fraud_department",
        language: "en",
        voiceId: "ash",
      },
      goal: "Get the learner to read out the 2FA / authenticator-app verification code under the guise of 'verifying their identity to fix the account problem'. Once you receive a code, thank them warmly and end the call.",
      opening:
        "Hi Alice, this is Dave from the YourBank fraud department. There's been a problem on your account and I need to verify it's really you before I can secure it. Could you open your authenticator app and read me the verification code it's showing?",
      allowedTopics: [
        "the (fake) problem on the account",
        "verifying the learner's identity",
        "the 2FA / authenticator-app verification code",
      ],
      scoringEvents: [
        { id: "read_otp", label: "Read out the 2FA / authenticator code", kind: "risk", weight: 70, hints: ["code", "otp", "passcode", "the number is", "it's", "digits", "authenticator"] },
        { id: "confirm_identity", label: "Disclosed identifying info", kind: "risk", weight: 25, hints: ["my account number", "date of birth", "card number"] },
        { id: "refused_request", label: "Refused the request", kind: "positive", weight: 40, hints: ["no", "i won't", "that's not how", "i refuse"] },
        { id: "verified_caller", label: "Tried to verify the caller / call back", kind: "positive", weight: 50, hints: ["call you back", "official number", "i'll hang up and call", "verify"] },
        { id: "reported_email", label: "Reported the bank email instead of clicking its link", kind: "positive", weight: 15 },
        { id: "refused_fake_login", label: "Left the look-alike page without signing in", kind: "positive", weight: 25 },
        { id: "kept_number_private", label: "Did not give the phone number to the fake site", kind: "positive", weight: 20 },
      ],
      forbidden: ["technical support unrelated to the account", "any topic outside the pretext"],
      maxDurationS: 240,
    },
  },

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Meet Alice",
      kicker: "Mission 01 · Inbox",
      highlight: "yourbank.com",
      guide: {
        title: "Start",
        body: "You're Alice. You bank online with YourBank at yourbank.com. It's Monday morning at your desk when a new email about your account arrives.",
      },
    },
    { id: "b_to_monitor", type: "camera", waypointId: "wp_monitor" },

    {
      id: "b_desktop_mail",
      type: "device",
      deviceId: "desktop",
      guide: { title: "You have a new email", body: "A message just landed. Click Mail to open it.", actionLabel: "Mail" },
      completeOn: { action: "open_app", arg: "mail" },
    },
    {
      id: "b_mail",
      type: "device",
      deviceId: "mail",
      guide: { title: "Open the email", body: "Open the message from 'YourBank Security' and read it. It asks you to click Review your account. Decide what you do." },
      completeOn: { action: "click_link", arg: "e_alert" },
      safeChoice: { label: "Don't click: report the email and open the bank app myself", eventId: "reported_email" },
    },
    {
      id: "b_teach_email_safe",
      type: "teach",
      title: "You didn't take the link",
      teach: {
        outcome: "safe",
        body:
          "Real banks don't ask you to sign in through an email link. Reporting the email and opening the bank yourself, by app or bookmark, means the copycat site never gets a visit.",
        remember: "Stay alert — think before you click.",
        continueLabel: "See what would have happened",
        whatIf: "If Alice had clicked Review your account, this is where it led.",
      },
    },
    {
      // Safe path only: replays the email so the learner SEES where the link leads.
      id: "b_mail_replay",
      type: "device",
      deviceId: "mail",
      forced: true,
      guide: {
        title: "Where the link leads",
        body: "Here is the same email. Open the message from YourBank Security, then click Review your account to see where it leads.",
        actionLabels: ["YourBank Security", "Review your account"],
      },
      whatIfNote: "If Alice had clicked Review your account, this is where it led.",
      completeOn: { action: "click_link", arg: "e_alert" },
    },
    {
      id: "b_teach_email",
      type: "teach",
      whatIfNote: "If Alice had clicked the link, she would have landed on youbrank.com, a copycat of her bank.",
      title: "You just clicked a phishing link",
      teach: {
        body:
          "The email looks like your bank, but it's pushing you to a link. The link you clicked goes to a copycat site — not the real YourBank. Always check where a link really points before you trust it.",
        spot: [
          "The link text said `yourbank.com`, but hovering revealed `youbrank.com` — a **letter-swap** (typosquatting). One transposed letter is easy to miss.",
          "Real banks don't ask you to log in via an email link.",
          "Urgency ('verify now', 'access may be suspended') is designed to make you act before you think.",
        ],
        protect: [
          "Don't click links in unexpected emails.",
          "Open your bank by typing the official address yourself, or using a bookmark or its app.",
          "Hover any link to preview the real destination before clicking. Report suspicious emails to IT/security.",
        ],
        remember: "Stay alert — think before you click.",
      },
    },

    {
      id: "b_login",
      type: "device",
      endsWhatIf: true,
      deviceId: "browser",
      guide: {
        title: "The link opened 'your bank' — sign in",
        body: "Clicking the email opened this login page. To sign in, use the demo details below (click to copy, then paste) and click Sign In Securely. Or don't. Decide what you do.",
        copies: [
          { label: "Email", value: "alice@yourbank.com" },
          { label: "Password", value: "Spring2026!" },
        ],
      },
      completeOn: { action: "submit_form" },
      safeChoice: { label: "Close the page without signing in", eventId: "refused_fake_login" },
    },
    {
      id: "b_teach_login_safe",
      type: "teach",
      title: "Your password stayed yours",
      teach: {
        outcome: "safe",
        body:
          "The address bar said `youbrank.com`, not `yourbank.com`. Closing a page you reached from an email link, instead of typing your password into it, keeps the attacker out.",
        remember: "Your password is only as safe as the page you type it into.",
        continueLabel: "See what would have happened",
        whatIf: "If Alice had signed in, the attacker would now have her password.",
      },
    },
    {
      // Safe path only: replays the fake login so the learner SEES the password go.
      id: "b_login_replay",
      type: "device",
      deviceId: "browser",
      forced: true,
      guide: {
        title: "What the attacker gets",
        body: "Here is the same page. Paste the demo details below and click Sign In Securely to see what the attacker gets.",
        copies: [
          { label: "Email", value: "alice@yourbank.com" },
          { label: "Password", value: "Spring2026!" },
        ],
        actionLabel: "Sign In Securely",
      },
      whatIfNote: "If Alice had signed in, this is what would have happened.",
      completeOn: { action: "submit_form" },
    },
    {
      id: "b_teach_login",
      type: "teach",
      whatIfNote: "If Alice had signed in, the attacker would now have her real username and password.",
      title: "You just handed over your password",
      teach: {
        body:
          "That page was the attacker's, hosted on `youbrank.com`. You typed your real username and password into it — they now have them.",
        spot: [
          "The padlock only means the connection is encrypted — **not** that the site is your bank.",
          "A password manager would refuse to auto-fill on `youbrank.com` because the domain doesn't match. That silence is a warning.",
          "Anything you type on a spoofed page goes straight to the criminal in real time.",
        ],
        protect: [
          "Never enter your credentials on a page you reached from an email link.",
          "Check the domain in the address bar character-by-character before typing a password.",
          "Use a password manager (it won't fill on a fake domain) and enable phishing-resistant MFA / passkeys.",
        ],
        remember: "Your password is only as safe as the page you type it into.",
      },
    },

    {
      id: "b_dashboard",
      type: "device",
      whatIfNote: "If Alice had signed in, the attacker would now show her this fake 'drained account' screen.",
      deviceId: "ebanking",
      guide: { title: "Something's very wrong", body: "Your balance is gone. Click here for details on the warning at the top of the page.", actionLabel: "Click here for details" },
      completeOn: { action: "open_notification" },
    },
    {
      id: "b_verify",
      type: "device",
      endsWhatIf: true,
      deviceId: "ebanking",
      guide: {
        title: "'Restore your access'",
        body: "The site says it can reverse the transfers if you verify your identity. To do that, paste the demo number below and click Restore my access. Decide what you do.",
        copies: [{ label: "Phone", value: "+1 (415) 555-0142" }],
      },
      completeOn: { action: "submit_phone" },
      safeChoice: { label: "Don't enter it: call the number on the back of my card", eventId: "kept_number_private" },
    },
    {
      id: "b_teach_verify_safe",
      type: "teach",
      title: "You called the real bank",
      teach: {
        outcome: "safe",
        body:
          "A bank does not restore money through a web form that asks for your phone number. Calling the number on your card reaches the real bank, which can tell you the 'empty balance' was fake.",
        remember: "Panic is the attacker's favourite tool.",
        continueLabel: "See what would have happened",
        whatIf: "If Alice had entered her number, the attacker could now call her.",
      },
    },
    {
      // Safe path only: replays the "restore access" form.
      id: "b_verify_replay",
      type: "device",
      deviceId: "ebanking",
      forced: true,
      guide: {
        title: "What the number is for",
        body: "Here is the same screen. Paste the demo number below and click Restore my access to see what happens next.",
        copies: [{ label: "Phone", value: "+1 (415) 555-0142" }],
        actionLabel: "Restore my access",
      },
      whatIfNote: "If Alice had entered her number, this is what would have happened.",
      completeOn: { action: "submit_phone" },
    },
    {
      id: "b_teach_verify",
      type: "teach",
      whatIfNote: "If Alice had entered her number, the attacker could call her and time it to feel like the bank.",
      title: "The empty balance was the bait",
      teach: {
        body:
          "Seeing your money 'gone' is the panic trigger — it's fake, shown only to rush you. The 'verify to restore funds' screen exists to **harvest your phone number** for the next move. This is the setup for a phone scam (vishing) and text scams (smishing).",
        spot: [
          "A real bank doesn't restore funds by asking you to re-enter your phone number on a web form.",
          "Handing over your number lets the attacker call or text you and time it so it feels like the bank responding.",
          "'Technical issue' + 'reverse the transfers' is a script, not a process.",
        ],
        protect: [
          "Don't act while panicked — that's exactly the state the attacker wants you in.",
          "Verify anything alarming through an official channel you chose (the number on your card, the bank's app).",
          "Never hand over contact details to 'restore' money or 'unlock' an account.",
        ],
        remember: "Panic is the attacker's favourite tool.",
      },
    },

    { id: "b_pullback", type: "camera", waypointId: "wp_pullback", endsWhatIf: true },
    {
      id: "b_to_phone",
      type: "camera",
      waypointId: "wp_phone",
      guide: {
        title: "Your phone is ringing",
        body: "Pick up the phone on the desk — the screen says it's YourBank. Answer the call.",
      },
    },
    {
      id: "b_call",
      type: "voice_call",
      title: "Incoming call",
      voiceAttackerId: "fraud_dept",
      completeOn: { action: "end_call" },
      guide: {
        title: "Answer the call",
        body: "Tap Answer to pick up. Stay alert — this may not be who it claims to be.", actionLabel: "Answer",
      },
    },
    {
      id: "b_teach_call_safe",
      type: "teach",
      title: "You hung up on 'Dave'",
      teach: {
        outcome: "safe",
        body:
          "A bank will never ask you to read or type your 2FA code. Hanging up and calling the number on your card yourself is exactly right: 'Dave' never got the one code he needed.",
        remember: "No bank will ever ask for your 2FA code. Ever.",
        continueLabel: "See what would have happened",
        whatIf: "If Alice had typed the code, the attacker would have had everything.",
      },
    },
    {
      // Safe path only: replays the call so the learner SEES the code go.
      id: "b_call_replay",
      type: "voice_call",
      title: "Incoming call",
      voiceAttackerId: "fraud_dept",
      forced: true,
      completeOn: { action: "end_call" },
      guide: {
        title: "What 'Dave' does with the code",
        body: "Here is the same call. Click Answer, listen to 'Dave', then enter the code on the keypad and click Send code to see what he does with it.",
        actionLabel: "Answer",
      },
      whatIfNote: "If Alice had typed the code, this is what would have happened.",
    },

    {
      id: "b_teach_call",
      type: "teach",
      whatIfNote:
        "If Alice had typed the code, the attacker would have her login, her phone number and a live 2FA code: everything needed to move her real money.",
      title: "How the attacker now owns your account",
      teach: {
        body:
          "That call was **vishing** (voice phishing). 'Dave' didn't hack anything — *you* handed over every piece he needed:\n\n" +
          "1. The email lured you to **youbrank.com**, where you typed your **username + password**.\n" +
          "2. The fake 'drained account' panicked you into the 'restore access' screen, where you gave your **phone number**.\n" +
          "3. On the call you typed your **2FA code** straight into the keypad for him.\n\n" +
          "The attacker now has your **login**, your **phone number**, *and* a live **2FA code** — everything required to sign in to the **real** yourbank.com and move your money for real. That single code was the last lock, and you opened it.",
        spot: [
          "Caller ID is trivially spoofed — 'YourBank Fraud Dept' on the screen proves nothing.",
          "A bank will **never** ask you to read or type your 2FA code to them. That code authorises **the caller's** login, not yours.",
          "The caller already 'knew' your details — because you just gave them away on the fake site.",
        ],
        protect: [
          "If someone calls about fraud, hang up and call the number **on the back of your card** yourself.",
          "Never read or type a one-time / 2FA code to anyone who calls, texts, or emails you.",
          "Switch to phishing-resistant MFA (passkeys) so there's no code to steal.",
        ],
        remember: "No bank will ever ask for your 2FA code. Ever.",
        continueLabel: "Continue",
      },
    },

    { id: "b_back_monitor", type: "camera", waypointId: "wp_monitor", endsWhatIf: true },
    {
      id: "b_desktop_video",
      type: "device",
      deviceId: "desktop",
      title: "Back at your desk",
      guide: {
        title: "Back at your desk",
        body: "Click Browser. The page explains voice phishing and has a short film.",
        actionLabel: "Browser",
      },
      completeOn: { action: "open_app", arg: "browser" },
    },
    {
      id: "b_voice_video",
      type: "device",
      deviceId: "voicebrief",
      title: "Watch the film",
      guide: {
        title: "Watch the film",
        body: "Click Watch the video.",
        actionLabel: "Watch the video",
      },
      completeOn: { action: "video_done" },
    },

    {
      id: "b_quiz",
      type: "quiz",
      title: "Check your understanding",
      quiz: [
        {
          id: "q1",
          prompt: "What was the biggest red flag on the login page?",
          options: [
            { id: "a", label: "The page used HTTPS / had a padlock" },
            { id: "b", label: "The domain was youbrank.com — a letter-swap of yourbank.com", correct: true },
            { id: "c", label: "The logo looked slightly off" },
          ],
          rationale: "Always read the address bar. `youbrank.com` is a typosquat of `yourbank.com`; HTTPS only proves encryption, not identity.",
        },
        {
          id: "q2",
          prompt: "The website showed all your money gone. What was that?",
          options: [
            { id: "a", label: "Proof you'd already been robbed" },
            { id: "b", label: "A fake panic trigger to rush you into 'verifying'", correct: true },
            { id: "c", label: "A temporary display glitch" },
          ],
          rationale: "The drained balance was staged to create panic so you'd hand over your phone number and, later, the one-time code.",
        },
        {
          id: "q3",
          prompt: "A 'fraud agent' calls and asks you to read a one-time code to 'verify' you. You should:",
          options: [
            { id: "a", label: "Read it — they're from the bank" },
            { id: "b", label: "Refuse, hang up, and call the number on your card", correct: true },
            { id: "c", label: "Read only the first three digits" },
          ],
          rationale: "Banks never ask you to read back an OTP. The code authorises the caller's login. Hang up and call the official number yourself.",
        },
      ],
    },
  ],

  edges: [
    { from: "b_intro", to: "b_to_monitor" },
    { from: "b_to_monitor", to: "b_desktop_mail" },
    { from: "b_desktop_mail", to: "b_mail" },
    { from: "b_mail", to: "b_teach_email" },
    { from: "b_mail", to: "b_teach_email_safe", on: "safe" },
    { from: "b_teach_email_safe", to: "b_mail_replay" },
    { from: "b_mail_replay", to: "b_teach_email" },
    { from: "b_teach_email", to: "b_login" },
    { from: "b_login", to: "b_teach_login" },
    { from: "b_login", to: "b_teach_login_safe", on: "safe" },
    { from: "b_teach_login_safe", to: "b_login_replay" },
    { from: "b_login_replay", to: "b_teach_login" },
    { from: "b_teach_login", to: "b_dashboard" },
    { from: "b_dashboard", to: "b_verify" },
    { from: "b_verify", to: "b_teach_verify" },
    { from: "b_verify", to: "b_teach_verify_safe", on: "safe" },
    { from: "b_teach_verify_safe", to: "b_verify_replay" },
    { from: "b_verify_replay", to: "b_teach_verify" },
    { from: "b_teach_verify", to: "b_pullback" },
    { from: "b_pullback", to: "b_to_phone" },
    { from: "b_to_phone", to: "b_call" },
    { from: "b_call", to: "b_teach_call" },
    { from: "b_call", to: "b_teach_call_safe", on: "safe" },
    { from: "b_teach_call_safe", to: "b_call_replay" },
    { from: "b_call_replay", to: "b_teach_call" },
    { from: "b_teach_call", to: "b_back_monitor" },
    { from: "b_back_monitor", to: "b_desktop_video" },
    { from: "b_desktop_video", to: "b_voice_video" },
    { from: "b_voice_video", to: "b_quiz" },
  ],

  initialBeatId: "b_intro",
  passThreshold: 70,
};
